import React, { useEffect, useState } from 'react';
import {
  Badge,
  BadgeProps,
  Box,
  Container,
  Divider,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { track } from '../../api/track';
import { Game } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';
import { Logo } from './Logo';

export const LobbyBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  '& .MuiBadge-badge': {
    fontSize: 16,
    border: `2px solid`,
    borderColor: '#9affd0',
    borderRadius: 4,
    padding: '5px 8px',
    color: '#9affd0',
    textShadow:
      '#9affd0 0px 0px 0px, #9affd0 0px 0px 0px, #9affd0 0px 0px 16px',
  },
}));

type ActiveTabs =
  | 'home'
  | 'profile'
  | 'lobby'
  | 'explore'
  | 'admin'
  | 'league'
  | 'tournament';

type PropTypes = {
  handleLogoutClick: () => void;
  onUpdateProfile: () => void;
  openGames?: Game[];
  openLeagueGames?: Game[];
  openTourneyGames?: Game[];
  profile: Profile;
};

export const TopNav = ({
  handleLogoutClick,
  onUpdateProfile,
  openGames,
  openLeagueGames,
  openTourneyGames,
  profile,
}: PropTypes) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const xlBreakpoint = useMediaQuery(theme.breakpoints.down('xl'));
  const lgBreakpoint = useMediaQuery(theme.breakpoints.down('lg'));
  const mdBreakpoint = useMediaQuery(theme.breakpoints.down('md'));

  const getActiveTab = () => {
    if (location.pathname === '/') return 'home';
    if (location.pathname.includes('profile')) return 'profile';
    if (location.pathname.includes('lobby')) return 'lobby';
    if (location.pathname.includes('explore')) return 'explore';
    if (location.pathname.includes('league')) return 'league';
    if (location.pathname.includes('tournament')) return 'tournament';
    if (location.pathname.includes('admin')) return 'admin';
  };

  const [activeTab, setActiveTab] = useState<ActiveTabs>(getActiveTab);

  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [location]);

  const HeaderTab = (isActiveTab: boolean, text: string) => (
    <Typography
      variant={isActiveTab ? 'body1led' : 'body1'}
      letterSpacing={5}
      sx={{
        color: !isActiveTab ? 'secondary.light' : null,
        filter: isActiveTab
          ? 'drop-shadow(0 0 15px #db3eb1) drop-shadow(0 0 50px #db3eb1) contrast(2)'
          : null,
        '&:hover': {
          color: '#ffbdff',
        },
      }}
    >
      {text}
    </Typography>
  );

  const LobbyHeaderTab = (isActiveTab: boolean, text: string) => (
    <Tooltip title={openGames?.length ? 'Open games available' : null}>
      <LobbyBadge badgeContent={openGames?.length}>
        <Typography
          variant={isActiveTab ? 'body1led' : 'body1'}
          letterSpacing={5}
          color={!isActiveTab ? 'secondary.light' : null}
          sx={{
            filter: isActiveTab
              ? 'drop-shadow(0 0 15px #db3eb1) drop-shadow(0 0 50px #db3eb1) contrast(2)'
              : null,
            '&:hover': {
              color: 'pink',
            },
          }}
        >
          {text}
        </Typography>
      </LobbyBadge>
    </Tooltip>
  );

  const LeagueHeaderTab = (isActiveTab: boolean, text: string) => (
    <LobbyBadge badgeContent={openLeagueGames?.length}>
      <Typography
        variant={isActiveTab ? 'body1led' : 'body1'}
        letterSpacing={5}
        sx={{
          color: !isActiveTab ? theme.palette.ranked.main : '#48d2ff',
          filter: isActiveTab
            ? 'drop-shadow(0 0 15px #0266bd) drop-shadow(0 0 50px #0266bd) contrast(2)'
            : null,
          '&:hover': {
            color: '#48d2ff',
          },
        }}
      >
        {text}
      </Typography>
    </LobbyBadge>
  );

  const TourneyHeaderTab = (isActiveTab: boolean, text: string) => (
    <LobbyBadge badgeContent={openTourneyGames?.length}>
      <Typography
        variant={isActiveTab ? 'body1led' : 'body1'}
        letterSpacing={5}
        sx={{
          color: !isActiveTab ? '#94ff00' : '#94ff00',
          filter: isActiveTab
            ? 'drop-shadow(0 0 15px #0266bd) drop-shadow(0 0 50px #0266bd) contrast(2)'
            : null,
          '&:hover': {
            color: '#94ff00',
          },
        }}
      >
        {text}
      </Typography>
    </LobbyBadge>
  );

  const handleProfileClick = async () => {
    if (!profile?.id) return;
    await track(profile?.id, 'header.profile.link');
    navigate(`/profile/overview/${profile?.id}`);
  };

  const handleMainClick = () => navigate('/');
  const handleLeaderboards = () => navigate('/explore/leaderboards');
  const handleLobby = () => navigate('/lobby');
  const handleAdminClick = () => navigate('/admin/dashboard');
  const handleTourney = () => navigate('/tournament');
  const handleLeagueClick = () => navigate('/league/lounge');

  const handleTopNavChange = (event: any, tab: ActiveTabs) => {
    setActiveTab(tab);
    if (tab === 'home') handleMainClick();
    if (tab === 'profile') handleProfileClick();
    if (tab === 'lobby') handleLobby();
    if (tab === 'explore') handleLeaderboards();
    if (tab === 'league') handleLeagueClick();
    if (tab === 'tournament') handleTourney();
    if (tab === 'admin') handleAdminClick();
  };

  return (
    <Container sx={{ '& .MuiContainer-root': { padding: 0 } }}>
      <Stack direction="row">
        <Box display={'flex'} justifyContent={'left'}>
          <Tabs
            value={false}
            onChange={handleTopNavChange}
            indicatorColor="secondary"
            sx={{
              width: '100%',
              height: '100%',
              '& .MuiTabs-flexContainer': { height: '100%' },
            }}
          >
            {lgBreakpoint ? (
              <Box
                display="flex"
                justifyContent={'center'}
                alignItems={'center'}
                paddingX={2}
              >
                <Logo handleLogoClick={handleMainClick} small />
              </Box>
            ) : (
              <Tab
                label={HeaderTab(activeTab === 'home', 'HOME')}
                value="home"
                sx={{
                  pr: 3,
                }}
              />
            )}
            <Divider
              flexItem
              orientation="vertical"
              sx={{
                height: '40%',
                alignSelf: 'center',
                borderRightWidth: 2,
              }}
            />
            <Tooltip title={lgBreakpoint ? 'Profile' : null}>
              <Tab
                label={HeaderTab(
                  activeTab === 'profile',
                  lgBreakpoint ? 'Prfl' : 'PROFILE'
                )}
                value="profile"
                sx={{ paddingX: lgBreakpoint ? 1 : 3 }}
              />
            </Tooltip>
            <Divider
              flexItem
              orientation="vertical"
              sx={{
                height: '40%',
                alignSelf: 'center',
                borderRightWidth: 2,
              }}
            />
            <Tooltip title={lgBreakpoint ? 'Lobby' : null}>
              <Tab
                label={LobbyHeaderTab(
                  activeTab === 'lobby',
                  lgBreakpoint ? 'Lby' : 'LOBBY'
                )}
                value="lobby"
                sx={{ paddingX: lgBreakpoint ? 1 : 3 }}
              />
            </Tooltip>
            <Divider
              flexItem
              orientation="vertical"
              sx={{
                height: '40%',
                alignSelf: 'center',
                borderRightWidth: 2,
              }}
            />
            <Tooltip title={lgBreakpoint ? 'Explore' : null}>
              <Tab
                label={HeaderTab(
                  activeTab === 'explore',
                  lgBreakpoint ? 'xplr' : 'EXPLORE'
                )}
                value="explore"
                sx={{ paddingX: lgBreakpoint ? 1 : 3 }}
              />
            </Tooltip>
            <Divider
              flexItem
              orientation="vertical"
              sx={{
                height: '40%',
                alignSelf: 'center',
                borderRightWidth: 2,
              }}
            />
            {/* <Tooltip title={lgBreakpoint ? 'League' : null}>
              <Tab
                label={LeagueHeaderTab(
                  activeTab === 'league',
                  lgBreakpoint ? 'leag' : 'LEAGUE'
                )}
                value="league"
                sx={{
                  paddingX: lgBreakpoint ? 1 : 3,
                }}
              />
            </Tooltip>
            <Divider
              flexItem
              orientation="vertical"
              sx={{
                height: '40%',
                alignSelf: 'center',
                borderRightWidth: 2,
              }}
            />
            <Tooltip title={lgBreakpoint ? 'Tournament' : null}>
              <Tab
                label={TourneyHeaderTab(
                  activeTab === 'tournament',
                  lgBreakpoint ? 'TOUR' : 'TOURNEY'
                )}
                value="tournament"
                sx={{
                  paddingX: lgBreakpoint ? 1 : 3,
                }}
              />
            </Tooltip> */}
            {profile.admin ? (
              <Divider
                flexItem
                orientation="vertical"
                sx={{
                  height: '40%',
                  alignSelf: 'center',
                  borderRightWidth: 2,
                }}
              />
            ) : null}
            {profile.admin ? (
              <Tab
                label={HeaderTab(activeTab === 'admin', 'ADMIN')}
                value="admin"
                sx={{ paddingX: lgBreakpoint ? 1 : 3 }}
              />
            ) : null}
          </Tabs>
        </Box>
      </Stack>
    </Container>
  );
};
